import { useState, useEffect, useCallback } from 'react'
import { ChevronLeft, ChevronRight, Loader2, Download, CreditCard, CheckCircle, XCircle, CalendarPlus, Ban } from 'lucide-react'
import toast from 'react-hot-toast'
import { fetchSubscriptions, updateSubscription } from '../services/adminService'

interface Subscription {
  id: string
  plan: string
  isActive: boolean
  startDate: string
  endDate: string
  pickupLimit: number
  user: { id: string; name: string; email: string; phone: string | null }
}

const FILTER_TABS = [
  { value: 'ALL', label: 'All' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'EXPIRED', label: 'Expired' },
]

const PAGE_SIZE = 10

function exportToCsv(headers: string[], rows: string[][], filename: string) {
  const csv = [headers.join(','), ...rows.map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))].join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export default function Subscriptions() {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('ALL')
  const [page, setPage] = useState(1)

  const loadSubscriptions = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, string | number> = { page, limit: PAGE_SIZE }
      if (filter === 'ACTIVE') params.active = 'true'
      if (filter === 'EXPIRED') params.active = 'false'

      const data = await fetchSubscriptions(params as Parameters<typeof fetchSubscriptions>[0])
      setSubscriptions(data?.subscriptions ?? [])
      setTotal(data?.pagination?.total ?? 0)
      setTotalPages(data?.pagination?.totalPages ?? 0)
    } catch {
      toast.error('Failed to load subscriptions')
    } finally {
      setLoading(false)
    }
  }, [filter, page])

  useEffect(() => { loadSubscriptions() }, [loadSubscriptions])

  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null)

  const handleExtend = async (subId: string) => {
    const input = prompt('Enter number of days to extend (default: 30):', '30')
    if (input === null) return
    const days = parseInt(input, 10)
    if (isNaN(days) || days <= 0) {
      toast.error('Please enter a valid positive number of days')
      return
    }
    setActionLoadingId(subId)
    try {
      await updateSubscription(subId, 'extend', days)
      toast.success(`Subscription extended by ${days} days`)
      loadSubscriptions()
    } catch {
      toast.error('Failed to extend subscription')
    } finally {
      setActionLoadingId(null)
    }
  }

  const handleCancel = async (subId: string) => {
    if (!confirm('Are you sure you want to cancel this subscription? This action cannot be undone.')) return
    setActionLoadingId(subId)
    try {
      await updateSubscription(subId, 'cancel')
      toast.success('Subscription cancelled')
      loadSubscriptions()
    } catch {
      toast.error('Failed to cancel subscription')
    } finally {
      setActionLoadingId(null)
    }
  }

  const activeCount = subscriptions.filter((s) => s.isActive).length
  const expiredCount = subscriptions.filter((s) => !s.isActive).length

  const handleExport = () => {
    if (subscriptions.length === 0) {
      toast.error('No subscriptions to export')
      return
    }
    const headers = ['Subscriber', 'Email', 'Phone', 'Plan', 'Status', 'Start Date', 'End Date', 'Pickups Included']
    const rows = subscriptions.map((s) => [
      s.user?.name ?? '—',
      s.user?.email ?? '',
      s.user?.phone ?? '',
      s.plan,
      s.isActive ? 'Active' : 'Expired',
      new Date(s.startDate).toLocaleDateString(),
      new Date(s.endDate).toLocaleDateString(),
      String(s.pickupLimit),
    ])
    exportToCsv(headers, rows, `subscriptions-${new Date().toISOString().slice(0, 10)}.csv`)
    toast.success('Subscriptions exported')
  }

  return (
    <div className="space-y-5">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: 'Total Subscriptions', value: total, icon: CreditCard, color: 'text-blue-600 bg-blue-50' },
          { label: 'Active', value: activeCount, icon: CheckCircle, color: 'text-green-700 bg-green-50' },
          { label: 'Expired', value: expiredCount, icon: XCircle, color: 'text-gray-500 bg-gray-50' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${color.split(' ')[1]} flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${color.split(' ')[0]}`} />
            </div>
            <div>
              <p className="text-xs text-[#6B7280] font-medium">{label}</p>
              <p className="text-xl font-bold text-[#1A1A2E]">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filter tabs + Export */}
      <div className="flex items-center justify-between">
        <div className="flex bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          {FILTER_TABS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => { setFilter(value); setPage(1) }}
              className={`px-4 py-2 text-xs font-semibold transition-colors ${
                filter === value ? 'bg-primary-600 text-white' : 'text-[#6B7280] hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          onClick={handleExport}
          className="flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-[#6B7280] hover:bg-gray-50 shadow-sm transition-colors"
        >
          <Download size={14} />
          Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <p className="text-sm font-semibold text-[#1A1A2E]">
            {total} subscription{total !== 1 ? 's' : ''}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#F8FAF9]">
                  {['Subscriber', 'Plan', 'Status', 'Start Date', 'End Date', 'Pickups Included', 'Actions'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {subscriptions.map((sub) => (
                  <tr key={sub.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-xs font-medium text-[#1A1A2E]">{sub.user?.name ?? '—'}</p>
                      <p className="text-[10px] text-[#6B7280]">{sub.user?.email ?? ''}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-semibold text-[#1A1A2E] capitalize">{sub.plan}</span>
                    </td>
                    <td className="px-4 py-3">
                      {sub.isActive ? (
                        <span className="flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full w-fit bg-green-100 text-green-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                          Active
                        </span>
                      ) : new Date(sub.endDate) > new Date() ? (
                        <span className="flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full w-fit bg-amber-100 text-amber-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                          Awaiting payment
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full w-fit bg-gray-100 text-gray-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                          Expired
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#6B7280] whitespace-nowrap">
                      {new Date(sub.startDate).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#6B7280] whitespace-nowrap">
                      {new Date(sub.endDate).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-xs font-semibold text-[#1A1A2E]">
                      {sub.pickupLimit}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {sub.isActive && (
                          <>
                            <button
                              onClick={() => handleExtend(sub.id)}
                              disabled={actionLoadingId === sub.id}
                              className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50 transition-colors"
                            >
                              {actionLoadingId === sub.id ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : (
                                <CalendarPlus size={12} />
                              )}
                              Extend
                            </button>
                            <button
                              onClick={() => handleCancel(sub.id)}
                              disabled={actionLoadingId === sub.id}
                              className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-red-600 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors"
                            >
                              {actionLoadingId === sub.id ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : (
                                <Ban size={12} />
                              )}
                              Cancel
                            </button>
                          </>
                        )}
                        {!sub.isActive && new Date(sub.endDate) <= new Date() && (
                          <button
                            onClick={() => handleExtend(sub.id)}
                            disabled={actionLoadingId === sub.id}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50 transition-colors"
                          >
                            {actionLoadingId === sub.id ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <CalendarPlus size={12} />
                            )}
                            Reactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {subscriptions.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-sm text-[#6B7280]">
                      No subscriptions found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
            <p className="text-xs text-[#6B7280]">
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, total)}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg text-[#6B7280] hover:bg-gray-100 disabled:opacity-40 transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNum: number
                if (totalPages <= 5) {
                  pageNum = i + 1
                } else if (page <= 3) {
                  pageNum = i + 1
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i
                } else {
                  pageNum = page - 2 + i
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`w-7 h-7 rounded-lg text-xs font-semibold transition-colors ${
                      pageNum === page ? 'bg-primary-600 text-white' : 'text-[#6B7280] hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || totalPages === 0}
                className="p-1.5 rounded-lg text-[#6B7280] hover:bg-gray-100 disabled:opacity-40 transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
